import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, apiPost, getCurrentUser } from '../utils/api';
import { connectSocket, disconnectSocket } from '../utils/socket';
import { 
  IoTimeOutline, 
  IoWarningOutline, 
  IoGameControllerOutline, 
  IoTrophyOutline, 
  IoHelpCircleOutline, 
  IoBulbOutline, 
  IoCheckmarkCircleOutline, 
  IoTimerOutline, 
  IoHomeOutline, 
  IoSparklesOutline, 
  IoHardwareChipOutline, 
  IoCreateOutline, 
  IoLockClosedOutline, 
  IoTvOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
  IoAlertCircleOutline,
  IoCloseCircleOutline,
  IoNotificationsOutline,
  IoInformationCircleOutline,
  IoListOutline,
  IoPlayOutline,
  IoSearchOutline,
  IoPrismOutline, // Using IoPrism instead of IoBrain
  IoChevronDownOutline // Added for accordion
} from 'react-icons/io5';
import './GameShow.css';

// Audio chime utility functions
let audioContext = null;
let audioEnabled = false;

// Initialize audio context after user interaction
const initializeAudio = async () => {
  if (audioContext) return audioContext;
  
  try {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Resume audio context if it's suspended (browser policy)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    
    audioEnabled = true;
    console.log('[AUDIO] Audio context initialized successfully');
    return audioContext;
  } catch (error) {
    console.error('[ERROR] Failed to initialize audio context:', error);
    audioEnabled = false;
    return null;
  }
};

const createAudioChime = async (frequency1, frequency2, duration = 300) => {
  try {
    const context = await initializeAudio();
    if (!context || !audioEnabled) {
      console.log('🔇 Audio not available or disabled');
      return;
    }
    
    const oscillator1 = context.createOscillator();
    const oscillator2 = context.createOscillator();
    const gainNode = context.createGain();
    
    oscillator1.frequency.setValueAtTime(frequency1, context.currentTime);
    oscillator2.frequency.setValueAtTime(frequency2, context.currentTime);
    
    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(context.destination);
    
    gainNode.gain.setValueAtTime(0.1, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + duration / 1000);
    
    oscillator1.start(context.currentTime);
    oscillator2.start(context.currentTime);
    oscillator1.stop(context.currentTime + duration / 1000);
    oscillator2.stop(context.currentTime + duration / 1000);
    
    console.log('🎵 Audio chime played successfully');
  } catch (error) {
    console.error('❌ Audio chime failed:', error);
  }
};

const playQuestionReleasedChime = async () => {
  // Pleasant ascending chime for question release (C major chord)
  try {
    await createAudioChime(523.25, 659.25, 400); // C5 + E5
    setTimeout(() => createAudioChime(783.99, 1046.50, 400), 150); // G5 + C6
    console.log('🔔 [AUDIO] Question released chime played');
  } catch (error) {
    console.log('🔇 Audio not supported or blocked by browser:', error.message);
  }
};

const playQuestionRevokedChime = async () => {
  // Gentle descending chime for question revocation
  try {
    await createAudioChime(880, 659.25, 300); // A5 + E5
    setTimeout(() => createAudioChime(523.25, 392, 400), 100); // C5 + G4
    console.log('🔕 [AUDIO] Question revoked chime played');
  } catch (error) {
    console.log('🔇 Audio not supported or blocked by browser:', error.message);
  }
};

// Prominent countdown component specifically for contestants
const ContestantCountdown = ({ contestantTimer }) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!contestantTimer) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const remaining = Math.max(0, contestantTimer.endTime - now);
      setTimeLeft(remaining);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [contestantTimer]);

  if (!contestantTimer || timeLeft <= 0) {
    return (
      <div className="contestant-countdown expired">
        <div className="countdown-time">0:00</div>
        <div className="countdown-status">Time's Up!</div>
      </div>
    );
  }

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div className="contestant-countdown active">
      <div className="countdown-time">{formattedTime}</div>
      <div className="countdown-status">Time Remaining</div>
    </div>
  );
};

// Add HomeViewerTimerDisplay component at the top level
const HomeViewerTimerDisplay = ({ homeViewerTimer }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [formattedTime, setFormattedTime] = useState('0:00');

  useEffect(() => {
    if (!homeViewerTimer) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const remaining = Math.max(0, homeViewerTimer.endTime - now);
      setTimeLeft(remaining);

      // Format the time
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setFormattedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [homeViewerTimer]);

  if (!homeViewerTimer) return null;

  return (
    <div className="home-viewer-timer-display">
      <div className="timer-header">
        <h3>
          <IoHomeOutline className="inline-icon" />
          Your Exclusive Time
        </h3>
        <p>Answer before other viewers can!</p>
      </div>
      <div className={`home-viewer-countdown ${timeLeft <= 0 ? 'expired' : 'active'}`}>
        <div className="countdown-time">{formattedTime}</div>
        <div className="countdown-status">HOME VIEWER TIME</div>
      </div>
    </div>
  );
};

// Helper function to check if contestant mode is active
const isContestantModeActive = (question) => {
  if (!question.contestantTimer) return false;
  const now = new Date().getTime();
  return now <= question.contestantTimer.endTime;
};

// Helper function to check if home viewer mode is active
const isHomeViewerModeActive = (question) => {
  if (!question.homeViewerTimer) return false;
  const now = new Date().getTime();
  return now <= question.homeViewerTimer.endTime;
};

function GameShow() {
  const [activeQuestions, setActiveQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardFilter, setLeaderboardFilter] = useState('question'); // 'question', 'today', 'alltime'
  const [answerDistribution, setAnswerDistribution] = useState([]);
  const [aiGrouping, setAiGrouping] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set()); // Track which questions user has answered
  const [currentUser, setCurrentUser] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [questionFilter, setQuestionFilter] = useState('all'); // 'all', 'active', 'expired'
  // Modal state
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [modalQuestion, setModalQuestion] = useState(null);
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const [chimeNotification, setChimeNotification] = useState(''); // New state for chime notifications
  const [audioPermissionRequested, setAudioPermissionRequested] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  // Add isLeaderboardOpen state
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [playerStats, setPlayerStats] = useState({
    daily: { rank: null, score: 0 },
    allTime: { rank: null, score: 0 }
  });
  const [statsTimeframe, setStatsTimeframe] = useState('daily'); // 'daily' or 'allTime'

  // Function to fetch player stats
  // Helper function to format rank (1st, 2nd, 3rd, etc)
  const formatRank = (rank) => {
    if (!rank) return '-';
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const v = rank % 100;
    return rank + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
  };

  const fetchPlayerStats = useCallback(async () => {
    console.log('📊 [FETCH PLAYER STATS] Starting fetchPlayerStats...');
    try {
      const API_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      console.log('📊 [FETCH PLAYER STATS] User ID:', userId, 'Token exists:', !!token);
      
      // Get user role to determine which endpoint to use
      const userRole = currentUser?.role_id;
      
      // Get today's date in YYYY-MM-DD format
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;

      // Fetch daily points directly from user_answers table
      const dailyResponse = await fetch(`${API_URL}/api/game-sessions/user-points/${userId}/daily`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fetch all-time points directly from user_answers table
      const allTimeResponse = await fetch(`${API_URL}/api/game-sessions/user-points/${userId}/all-time`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📊 [FETCH PLAYER STATS] Daily response status:', dailyResponse.status, dailyResponse.ok);
      console.log('📊 [FETCH PLAYER STATS] All-time response status:', allTimeResponse.status, allTimeResponse.ok);
      
      let dailyData = { total_points: 0 };
      let allTimeData = { total_points: 0 };
      let dailyLeaderboard = [];
      let allTimeLeaderboard = [];
      
      // Handle daily data
      if (dailyResponse.ok) {
        dailyData = await dailyResponse.json();
        console.log('📊 [FETCH PLAYER STATS] Daily data received:', dailyData);
      } else {
        console.error('📊 [FETCH PLAYER STATS] Daily response failed:', dailyResponse.status, dailyResponse.statusText);
      }
      
      // Handle all-time data
      if (allTimeResponse.ok) {
        allTimeData = await allTimeResponse.json();
        console.log('📊 [FETCH PLAYER STATS] All-time data received:', allTimeData);
      } else {
        console.error('📊 [FETCH PLAYER STATS] All-time response failed:', allTimeResponse.status, allTimeResponse.statusText);
      }

      // Get daily rank by fetching all users' daily points
      const dailyLeaderboardResponse = await fetch(`${API_URL}/api/game-sessions/leaderboard-daily`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (dailyLeaderboardResponse.ok) {
        dailyLeaderboard = await dailyLeaderboardResponse.json();
        console.log('📊 [FETCH PLAYER STATS] Daily leaderboard received:', dailyLeaderboard.length, 'entries');
        console.log('📊 [FETCH PLAYER STATS] Daily leaderboard data:', dailyLeaderboard);
        console.log('📊 [FETCH PLAYER STATS] Looking for user ID in daily:', userId);
        
        // Check if user exists in daily leaderboard
        const dailyUserEntry = dailyLeaderboard.find(entry => 
          entry && entry.user_id && entry.user_id.toString() === userId
        );
        console.log('📊 [FETCH PLAYER STATS] Found daily user entry:', dailyUserEntry);
      } else {
        console.error('📊 [FETCH PLAYER STATS] Daily leaderboard failed:', dailyLeaderboardResponse.status);
      }
      
      const dailyRank = dailyLeaderboard.findIndex(entry => 
        entry && entry.user_id && entry.user_id.toString() === userId
      ) + 1;
      
      console.log('📊 [FETCH PLAYER STATS] Calculated daily rank:', dailyRank);

      // Get all-time rank by fetching all users' all-time points
      const allTimeLeaderboardResponse = await fetch(`${API_URL}/api/game-sessions/leaderboard-all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (allTimeLeaderboardResponse.ok) {
        allTimeLeaderboard = await allTimeLeaderboardResponse.json();
        console.log('📊 [FETCH PLAYER STATS] All-time leaderboard received:', allTimeLeaderboard.length, 'entries');
        console.log('📊 [FETCH PLAYER STATS] All-time leaderboard data:', allTimeLeaderboard);
        console.log('📊 [FETCH PLAYER STATS] Looking for user ID:', userId);
        
        // Check if user exists in leaderboard
        const userEntry = allTimeLeaderboard.find(entry => 
          entry && entry.user_id && entry.user_id.toString() === userId
        );
        console.log('📊 [FETCH PLAYER STATS] Found user entry:', userEntry);
      } else {
        console.error('📊 [FETCH PLAYER STATS] All-time leaderboard failed:', allTimeLeaderboardResponse.status);
      }
      
      const allTimeRank = allTimeLeaderboard.findIndex(entry => 
        entry && entry.user_id && entry.user_id.toString() === userId
      ) + 1;
      
      console.log('📊 [FETCH PLAYER STATS] Calculated all-time rank:', allTimeRank);

      const newStats = {
        daily: {
          rank: dailyRank > 0 ? dailyRank : null,
          score: parseInt(dailyData.total_points) || 0
        },
        allTime: {
          rank: allTimeRank > 0 ? allTimeRank : null,
          score: parseInt(allTimeData.total_points) || 0
        }
      };
      
      console.log('📊 [FETCH PLAYER STATS] New stats calculated:', newStats);
      setPlayerStats(newStats);
      console.log('📊 [FETCH PLAYER STATS] Player stats updated successfully');
    } catch (error) {
      console.error('Error fetching player stats:', error);
    }
  }, [currentUser]);

  // Fetch player stats on mount and when questions change
  useEffect(() => {
    if (currentUser) {
      fetchPlayerStats();
    }
  }, [fetchPlayerStats, currentUser, activeQuestions]);

  // Individual Question Timer Component
  const QuestionTimer = ({ endTime, contestantTimer, homeViewerTimer, userRole }) => {
    const [countdown, setCountdown] = useState('');
    const [isExpired, setIsExpired] = useState(false);
    const [timerType, setTimerType] = useState('normal');

    useEffect(() => {
      const updateCountdown = () => {
        const now = new Date().getTime();
        
        // Determine which timer to use based on user role and timer availability
        let targetEndTime = endTime;
        let currentTimerType = 'normal';
        
        // Check for home viewer timer first
        if (homeViewerTimer && homeViewerTimer.endTime > now) {
          if (userRole === 2) {
            // Home viewer - use home viewer timer
            targetEndTime = new Date(homeViewerTimer.endTime).toISOString();
            currentTimerType = 'home-viewer';
          } else {
            // Non-home viewer during home viewer timer - blocked
            currentTimerType = 'home-viewer-blocked';
            setTimerType(currentTimerType);
            setCountdown('Home Viewer Mode');
            setIsExpired(false);
            return;
          }
        } else if (contestantTimer && contestantTimer.endTime > now) {
          if (userRole === 3) {
            // Contestant - use contestant timer
            targetEndTime = new Date(contestantTimer.endTime).toISOString();
            currentTimerType = 'contestant';
          } else {
            // Non-contestant during contestant timer - blocked
            currentTimerType = 'contestant-blocked';
            setTimerType(currentTimerType);
            setCountdown('Contestant Mode');
            setIsExpired(false);
            return;
          }
        }
        
        setTimerType(currentTimerType);
        const endTimeMs = new Date(targetEndTime).getTime();
        const timeLeft = endTimeMs - now;

        if (timeLeft <= 0) {
          setCountdown('00:00:00');
          setIsExpired(true);
          return;
        }

        const hours = Math.floor(timeLeft / (1000 * 60 * 60));
        const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

        const formattedTime = 
          `${hours.toString().padStart(2, '0')}:` +
          `${minutes.toString().padStart(2, '0')}:` +
          `${seconds.toString().padStart(2, '0')}`;

        setCountdown(formattedTime);
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);

      return () => clearInterval(interval);
    }, [endTime, contestantTimer, homeViewerTimer, userRole]);

    if (!endTime) return null;

    const getTimerClass = () => {
      if (timerType === 'home-viewer-blocked' || timerType === 'contestant-blocked') return 'blocked';
      if (timerType === 'home-viewer') return 'home-viewer-timer';
      if (timerType === 'contestant') return 'contestant-timer';
      if (isExpired) return 'expired';
      return '';
    };

    const getTimerText = () => {
      if (timerType === 'home-viewer-blocked') return 'Home viewer-only mode active';
      if (timerType === 'contestant-blocked') return 'Contestant-only mode active';
      if (timerType === 'home-viewer') return `Home viewer timer: ${countdown}`;
      if (timerType === 'contestant') return `Contestant timer: ${countdown}`;
      if (isExpired) return 'Expired';
      return `Time remaining: ${countdown}`;
    };

    return (
      <div className={`question-timer ${getTimerClass()}`}>
        <IoTimeOutline className="timer-icon-small" />
        <span className="timer-text">{getTimerText()}</span>
      </div>
    );
  };

  // Get the most urgent active question (closest to ending)
  const getCurrentActiveQuestion = () => {
    const now = new Date().getTime();
    const activeQuestionsInTime = activeQuestions.filter(q => {
      const endTime = new Date(q.end_time).getTime();
      return endTime > now;
    });

    if (activeQuestionsInTime.length === 0) return null;

    // Sort by end time to get the most urgent one
    return activeQuestionsInTime.sort((a, b) => 
      new Date(a.end_time).getTime() - new Date(b.end_time).getTime()
    )[0];
  };

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    
    // Get current user info to check role
    const user = getCurrentUser();
    setCurrentUser(user);
    
    // Fetch questions after user is set
    console.log('[INITIAL LOAD] Fetching questions with filter:', questionFilter);
    fetchActiveQuestionsForUser(user);
  }, [navigate]);



  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!currentUser) return;

    console.log('[SOCKET SETUP] Setting up socket connection for user:', currentUser.name, 'role:', currentUser.role_id);
    
    // Connect socket with user ID
    const socket = connectSocket(currentUser.id);
    socketRef.current = socket;

    // Listen for question releases
    socket.on('question-released', async (data) => {
      console.log('🔔 [SOCKET] Question released event received:', data);
      
      // Play chime only for relevant user types
      const shouldPlayChime = (
        (data.type === 'contestant' && currentUser.role_id === 3) || // Gameshow contestants
        (data.type === 'home-viewer' && currentUser.role_id === 2) || // Home viewers
        (currentUser.role_id === 1) // Admins hear all chimes
      );
      
      if (shouldPlayChime) {
        console.log('🔔 [AUDIO] Playing question released chime for user type:', currentUser.role_id);
        await requestAudioPermission(); // Ensure audio is initialized
        await playQuestionReleasedChime();
        
        // Show visual notification
        const message = data.type === 'contestant' ? 'Question released to contestants!' : 'Question released to home viewers!';
        setChimeNotification(message);
        setTimeout(() => setChimeNotification(''), 3000); // Clear after 3 seconds
      }
      
      // Simple refresh for all users - let the backend handle filtering
      console.log('[SOCKET] Refreshing questions for all users');
      fetchActiveQuestionsForUser(currentUser);
      fetchPlayerStats(); // Update player rank and score immediately
    });

    // Listen for question revocations
    socket.on('question-revoked', async (data) => {
      console.log('🔕 [SOCKET] Question revoked event received:', data);
      
      // Play chime only for relevant user types
      const shouldPlayChime = (
        (data.type === 'contestant' && currentUser.role_id === 3) || // Gameshow contestants
        (data.type === 'home-viewer' && currentUser.role_id === 2) || // Home viewers
        (currentUser.role_id === 1) // Admins hear all chimes
      );
      
      if (shouldPlayChime) {
        console.log('🔕 [AUDIO] Playing question revoked chime for user type:', currentUser.role_id);
        await requestAudioPermission(); // Ensure audio is initialized
        await playQuestionRevokedChime();
        
        // Show visual notification
        const message = data.type === 'contestant' ? 'Question revoked from contestants!' : 'Question revoked from home viewers!';
        setChimeNotification(message);
        setTimeout(() => setChimeNotification(''), 3000); // Clear after 3 seconds
      }
      
      // Refresh questions and update player stats in real-time
      console.log('🔄 [SOCKET] Calling fetchActiveQuestionsForUser and fetchPlayerStats');
      fetchActiveQuestionsForUser(currentUser);
      fetchPlayerStats(); // Update player rank and score immediately
      console.log('✅ [SOCKET] fetchPlayerStats called successfully');
    });

    // Listen for timer events
    socket.on('timer-started', (data) => {
      console.log('⏰ [SOCKET] Timer started event received:', data);
      fetchActiveQuestionsForUser(currentUser);
    });

    // Listen for general question updates
    socket.on('questions-updated', (data) => {
      console.log('🔄 [SOCKET] Questions updated event received:', data);
      fetchActiveQuestionsForUser(currentUser);
      fetchPlayerStats(); // Update player rank and score immediately
    });

    // Listen for timer expiration events
    socket.on('timer-expired', (data) => {
      console.log('⏰ [SOCKET] Timer expired event received:', data);
      fetchActiveQuestionsForUser(currentUser);
      fetchPlayerStats(); // Update player rank and score immediately
    });

    // Listen for show answers events
    socket.on('show-answers', (data) => {
      console.log('📊 [SOCKET] Show answers event received:', data);
      fetchActiveQuestionsForUser(currentUser);
      fetchPlayerStats(); // Update player rank and score immediately
    });

    // Cleanup on unmount
    return () => {
      console.log('[SOCKET CLEANUP] Removing socket listeners');
      if (socketRef.current) {
        socketRef.current.off('question-released');
        socketRef.current.off('question-revoked');
        socketRef.current.off('timer-started');
        socketRef.current.off('questions-updated');
        socketRef.current.off('timer-expired');
        socketRef.current.off('show-answers');
        disconnectSocket();
        socketRef.current = null;
      }
    };
  }, [currentUser]);

  // Check answered status when questions change
  useEffect(() => {
    if (activeQuestions.length > 0 && currentUser) {
      checkAnsweredStatusForAllQuestions();
    }
  }, [activeQuestions, currentUser]);

  const fetchActiveQuestionsForUser = async (user) => {
    try {
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      
      // Use different endpoints based on user role
      const endpoint = user && user.role_id === 3 
        ? '/api/game-sessions/contestant-questions'  // Game show users only see released questions
        : '/api/game-sessions/active-questions';     // Admins and regular users see all questions
      
      console.log('[FETCH QUESTIONS] User role:', user?.role_id, 'Using endpoint:', endpoint);
      console.log('[FETCH QUESTIONS] Token available:', !!token);
      console.log('[FETCH QUESTIONS] Filter:', questionFilter);
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Add filter parameter and cache-busting timestamp to URL
      const timestamp = new Date().getTime();
      const url = `${API_URL}${endpoint}?filter=${questionFilter}&_t=${timestamp}`;
      
      console.log('[FETCH QUESTIONS] Fetching from URL:', url);
      
      const res = await fetch(url, {
        headers: headers,
        cache: 'no-cache' // Prevent browser caching
      });
      
      if (!res.ok) throw new Error('Failed to fetch active questions');
      const data = await res.json();
      
      console.log('[FETCH QUESTIONS] Received data:', data);
      console.log('[FETCH QUESTIONS] Response status:', res.status);
      console.log('[FETCH QUESTIONS] Request URL was:', url);
      
      // Handle both array format and object with questions property
      const questionsList = Array.isArray(data) ? data : (data.questions || []);
      
      // Filter out expired questions
      const now = new Date().getTime();
      const activeQuestionsList = questionsList.filter(question => {
        const endTime = new Date(question.end_time).getTime();
        const isExpired = now > endTime;

        // For home viewers during home viewer time
        if (user?.role_id === 2 && question.homeViewerTimer) {
          const homeViewerEndTime = question.homeViewerTimer.endTime;
          if (homeViewerEndTime > now) {
            return true; // Show during home viewer time
          }
        }

        // For contestants during contestant time
        if (user?.role_id === 3 && question.contestantTimer) {
          const contestantEndTime = question.contestantTimer.endTime;
          if (contestantEndTime > now) {
            return true; // Show during contestant time
          }
        }

        // For regular expiration check
        return !isExpired;
      });

      console.log('[FETCH QUESTIONS] Filtered active questions:', activeQuestionsList.length, 'out of', questionsList.length);
      
      // Ensure each question has an 'id' property
      const questionsWithId = activeQuestionsList.map(q => ({ ...q, id: q.id || q.question_id }));
      console.log('[FETCH QUESTIONS] Setting active questions:', questionsWithId.length, 'questions');
      
      setActiveQuestions(questionsWithId);
    } catch (err) {
      console.error('[FETCH QUESTIONS] Error:', err);
      setError('Could not load active questions.');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveQuestions = async () => {
    // Use the current user from state
    await fetchActiveQuestionsForUser(currentUser);
  };

  const fetchLeaderboard = async (gameSessionId) => {
    try {
      console.log('[FRONTEND] Fetching leaderboard for game session:', gameSessionId);
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const url = `${API_URL}/api/game-sessions/leaderboard/${gameSessionId}`;
      console.log('[FRONTEND] Leaderboard URL:', url);
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch leaderboard');
      const data = await res.json();
      console.log('[FRONTEND] Leaderboard data received:', data);
      setLeaderboard(data);
    } catch (err) {
      console.error('Could not load leaderboard:', err);
      setLeaderboard([]);
    }
  };

  const fetchDailyLeaderboard = async () => {
    try {
      console.log('[FRONTEND] Fetching daily leaderboard');
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/game-sessions/leaderboard-daily`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch daily leaderboard');
      const data = await res.json();
      console.log('[FRONTEND] Daily leaderboard data received:', data);
      
      // Transform data to match leaderboard format
      const transformedData = data.map(user => ({
        username: user.username,
        awarded_points: user.total_points || user.awarded_points || 0,
        role_id: user.role_id || 1, // Default to admin if not specified
        user_id: user.user_id
      }));
      
      setLeaderboard(transformedData);
    } catch (err) {
      console.error('Could not load daily leaderboard:', err);
      setLeaderboard([]);
    }
  };

  const fetchAllTimeLeaderboard = async () => {
    try {
      console.log('[FRONTEND] Fetching all-time leaderboard');
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/game-sessions/leaderboard-all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch all-time leaderboard');
      const data = await res.json();
      console.log('[FRONTEND] All-time leaderboard data received:', data);
      
      // Transform data to match leaderboard format
      const transformedData = data.map(user => ({
        username: user.username || user.name,
        awarded_points: user.total_points || user.total_score || user.awarded_points || 0,
        role_id: user.role_id || 1, // Default to admin if not specified
        user_id: user.user_id || user.id
      }));
      
      setLeaderboard(transformedData);
    } catch (err) {
      console.error('Could not load all-time leaderboard:', err);
      setLeaderboard([]);
    }
  };

  const handleLeaderboardFilterChange = async (filter) => {
    setLeaderboardFilter(filter);
    
    if (filter === 'question' && selectedQuestion) {
      await fetchLeaderboard(selectedQuestion.game_session_id);
    } else if (filter === 'today') {
      await fetchDailyLeaderboard();
    } else if (filter === 'alltime') {
      await fetchAllTimeLeaderboard();
    }
  };

  const fetchAnswerDistribution = async (gameSessionId) => {
    // Only fetch answer distribution for admin users
    if (!currentUser || currentUser.role_id !== 1) {
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/game-sessions/answer-distribution/${gameSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setAnswerDistribution(data);
      }
    } catch (err) {
      console.error('Could not load answer distribution:', err);
    }
  };

  const fetchAiGrouping = async (gameSessionId) => {
    // Only fetch AI grouping for admin users
    if (!currentUser || currentUser.role_id !== 1) {
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/api/game-sessions/ai-grouping/${gameSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setAiGrouping(data);
      }
    } catch (err) {
      console.error('Could not load AI grouping details:', err);
    }
  };

  const checkIfUserAnswered = async (gameSessionId) => {
    try {
      const API_URL = process.env.REACT_APP_SERVER_URL 
        ? process.env.REACT_APP_SERVER_URL 
        : 'http://localhost:5000';
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      const res = await fetch(`${API_URL}/api/game-sessions/user-answer/${gameSessionId}/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHasAnswered(data.hasAnswered);
      }
    } catch (err) {
      console.error('Could not check user answer status:', err);
    }
  };

  // Check answered status for all questions
  const checkAnsweredStatusForAllQuestions = async () => {
    if (!currentUser || activeQuestions.length === 0) return;
    
    const API_URL = process.env.REACT_APP_SERVER_URL 
      ? process.env.REACT_APP_SERVER_URL 
      : 'http://localhost:5000';
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');
    
    const answeredSet = new Set();
    
    // Check each question's answer status
    for (const question of activeQuestions) {
      try {
        const res = await fetch(`${API_URL}/api/game-sessions/user-answer/${question.game_session_id}/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.hasAnswered) {
            answeredSet.add(question.game_session_id);
          }
        }
      } catch (err) {
        console.error('Could not check answer status for question:', question.id, err);
      }
    }
    
    setAnsweredQuestions(answeredSet);
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setError('');
    setAnswerDistribution([]); // Clear previous distribution
    setAiGrouping(null); // Clear previous AI grouping
    
    // Set filter to question and fetch appropriate leaderboard
    setLeaderboardFilter('question');
    fetchLeaderboard(question.game_session_id);
    fetchAnswerDistribution(question.game_session_id);
    fetchAiGrouping(question.game_session_id);
    checkIfUserAnswered(question.game_session_id);
  };

  const handleAnswerButtonClick = (question) => {
    setModalQuestion(question);
    setAnswer('');
    setShowAnswerModal(true);
  };

  const closeAnswerModal = () => {
    setShowAnswerModal(false);
    setModalQuestion(null);
    setAnswer('');
    setError('');
  };

  // Helper function to check if a question is expired for the current user
  const isQuestionExpired = (question) => {
    if (!question) return true; // If no question, consider it expired
    if (!currentUser) return true; // If no user, consider it expired
    
    const now = new Date().getTime();
    const normalEndTime = new Date(question.end_time).getTime();
    
    // Check if there's an active home viewer timer
    if (question.homeViewerTimer) {
      const homeViewerEndTime = question.homeViewerTimer.endTime;
      
      if (currentUser.role_id === 2) {
        // Home viewer user - check home viewer timer if active, otherwise normal timer
        if (homeViewerEndTime > now) {
          return false; // Home viewer timer is active, not expired for home viewer
        } else {
          return now > normalEndTime; // Fall back to normal timer
        }
      } else {
        // Non-home viewer user - blocked if home viewer timer is active
        if (homeViewerEndTime > now) {
          return true; // Blocked during home viewer time
        }
        // Fall through to check contestant timer
      }
    }
    
    // Check if there's an active contestant timer
    if (question.contestantTimer) {
      const contestantEndTime = question.contestantTimer.endTime;
      
      if (currentUser.role_id === 3) {
        // Contestant user - check contestant timer if active, otherwise normal timer
        if (contestantEndTime > now) {
          return false; // Contestant timer is active, not expired for contestant
        } else {
          return now > normalEndTime; // Fall back to normal timer
        }
      } else {
        // Non-contestant user - blocked if contestant timer is active
        if (contestantEndTime > now) {
          return true; // Blocked during contestant time
        }
      }
    }
    
    // No active timers, use normal expiration
    return now > normalEndTime;
  };

  const currentActiveQuestion = getCurrentActiveQuestion();

  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    if (!modalQuestion || !answer.trim()) return;
    
    // Check if question has expired for this user
    if (isQuestionExpired(modalQuestion)) {
      if (isContestantModeActive(modalQuestion) && currentUser?.role_id !== 3) {
        setError('This question is currently in contestant-only mode. Only game show contestants can answer right now.');
      } else {
        setError('This question has expired. You can no longer submit answers.');
      }
      return;
    }
    
    try {
      // Use authenticated API call
      const response = await apiPost('/api/game-sessions/answers', {
        gameSessionId: modalQuestion.game_session_id,
        answerText: answer.trim()
      });

      // Show points awarded with AI information
      alert(`Answer submitted! You earned ${response.awardedPoints} points!\n\nℹ️ AI-Powered Scoring: Your answer is automatically grouped with similar responses (misspellings, synonyms, abbreviations) to ensure fair scoring.`);
      
      // Close modal and update state
      closeAnswerModal();
      setHasAnswered(true);
      
      // Add this question to answered questions set
      setAnsweredQuestions(prev => new Set([...prev, modalQuestion.game_session_id]));
      
      // Refresh leaderboard, answer distribution, and AI grouping for this question
      if (selectedQuestion && selectedQuestion.id === modalQuestion.id) {
        fetchLeaderboard(selectedQuestion.game_session_id);
        fetchAnswerDistribution(selectedQuestion.game_session_id);
        fetchAiGrouping(selectedQuestion.game_session_id);
        checkIfUserAnswered(selectedQuestion.game_session_id);
      }
    } catch (error) {
      setError(error.message || 'Failed to submit answer');
    }
  };

  // Request audio permission on first user interaction
  const requestAudioPermission = async () => {
    try {
      await initializeAudio();
      setAudioPermissionRequested(true);
      setAudioEnabled(true); // Set initial state to enabled when permission is granted
      console.log('Audio permission granted and initialized');
    } catch (error) {
      console.error('Error requesting audio permission:', error);
      setAudioPermissionRequested(true);
      setAudioEnabled(false); // Set to false if there's an error
    }
  };

  // Initialize audio on component mount and first interaction
  useEffect(() => {
    const handleFirstInteraction = () => {
      requestAudioPermission();
      // Remove listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };

    // Add event listeners for first user interaction
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [audioPermissionRequested]);

  // Check if current user is admin or game show user
  const isAdmin = currentUser && currentUser.role_id === 1;
  const isGameShowUser = currentUser && currentUser.role_id === 3;
  const isHomeViewer = currentUser && currentUser.role_id === 2;

  if (loading) {
    return <div className="loading">Loading questions...</div>;
  }

  return (
    <div className="game-show">
      {/* Player Stats */}
      <div className="player-stats">
        <div className="player-stat-card" onClick={() => setStatsTimeframe(statsTimeframe === 'daily' ? 'allTime' : 'daily')}>
          <IoTrophyOutline className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">Your {statsTimeframe === 'daily' ? 'Daily' : 'All-Time'} Rank</span>
            <span className="stat-value">{formatRank(playerStats[statsTimeframe].rank)}</span>
          </div>
        </div>
        <div className="player-stat-card" onClick={() => setStatsTimeframe(statsTimeframe === 'daily' ? 'allTime' : 'daily')}>
          <IoSparklesOutline className="stat-icon" />
          <div className="stat-content">
            <span className="stat-label">{statsTimeframe === 'daily' ? 'Daily' : 'All-Time'} Score</span>
            <span className="stat-value">{playerStats[statsTimeframe].score} pts</span>
          </div>
        </div>
      </div>

      {/* Mobile-optimized Chime Notification */}
      {chimeNotification && (
        <div className="chime-notification">
          <IoNotificationsOutline className="notification-icon" />
          {chimeNotification}
        </div>
      )}

      <div className="active-questions-container">
        {/* Header */}
        <div className="game-header">
          <h2>Active Questions</h2>
          
          {/* Status Bar */}
          <div className="status-bar">
            <div className="active-count">
              <IoGameControllerOutline className="inline-icon" />
              Active: {activeQuestions.filter(q => !isQuestionExpired(q)).length}
            </div>

            {!audioPermissionRequested ? (
              <button className="audio-badge muted" onClick={requestAudioPermission}>
                <IoVolumeMuteOutline className="inline-icon" />
                Enable Audio
              </button>
            ) : (
              <button 
                className={`audio-badge ${audioEnabled ? 'enabled' : 'muted'}`}
                onClick={() => {
                  if (audioContext) {
                    if (audioContext.state === 'suspended') {
                      audioContext.resume();
                      setAudioEnabled(true);
                    } else if (audioContext.state === 'running') {
                      audioContext.suspend();
                      setAudioEnabled(false);
                    }
                  }
                }}
              >
                {audioEnabled ? (
                  <>
                    <IoVolumeHighOutline className="inline-icon" />
                    Audio Enabled
                  </>
                ) : (
                  <>
                    <IoVolumeMuteOutline className="inline-icon" />
                    Audio Disabled
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-options">
            <button
              onClick={() => setQuestionFilter('all')}
              className={`filter-option ${questionFilter === 'all' ? 'active' : ''}`}
            >
              <IoListOutline className="filter-icon" />
              All
            </button>
            <button
              onClick={() => setQuestionFilter('active')}
              className={`filter-option ${questionFilter === 'active' ? 'active' : ''}`}
            >
              <IoPlayOutline className="filter-icon" />
              Active
            </button>
            <button
              onClick={() => setQuestionFilter('expired')}
              className={`filter-option ${questionFilter === 'expired' ? 'active' : ''}`}
            >
              <IoTimeOutline className="filter-icon" />
              Expired
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <IoAlertCircleOutline className="inline-icon" />
            {error}
          </div>
        )}

        {/* Questions List or Empty State */}
        {activeQuestions.filter(q => {
          const expired = isQuestionExpired(q);
          if (questionFilter === 'active') return !expired;
          if (questionFilter === 'expired') return expired;
          return true;
        }).length === 0 ? (
          <div className="no-questions-message">
            <IoHelpCircleOutline className="message-icon" />
            <p>No questions available</p>
          </div>
        ) : (
          <div className="questions-list">
            <ul className="active-questions-list">
              {activeQuestions
                .filter(q => {
                  const expired = isQuestionExpired(q);
                  if (questionFilter === 'active') return !expired;
                  if (questionFilter === 'expired') return expired;
                  return true;
                })
                .map((q) => {
                  const expired = isQuestionExpired(q);
                  return (
                    <li 
                      key={q.id} 
                      className={`active-question-item ${expired ? 'expired' : 'new'}`}
                      onClick={() => !expired && handleQuestionClick(q)}
                    >
                      <div className="question-content">
                        <strong>{q.question_text}</strong>
                        
                        {q.homeViewerTimer && (
                          <div className="home-viewer-timer">
                            <IoTimeOutline className="timer-icon" />
                            Home viewer timer: {new Date(q.homeViewerTimer.endTime).toLocaleTimeString()}
                          </div>
                        )}
                        
                        <QuestionTimer 
                          endTime={q.end_time}
                          contestantTimer={q.contestantTimer}
                          homeViewerTimer={q.homeViewerTimer}
                          userRole={currentUser?.role_id}
                        />
                      </div>

                      {!expired && (
                        <button 
                          className="view-question-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuestionClick(q);
                            handleAnswerButtonClick(q);
                          }}
                        >
                          <IoSearchOutline className="view-question-icon" />
                          Answer Question
                        </button>
                      )}

                      {expired && (
                        <div className="expired-notice">
                          <IoTimeOutline className="inline-icon" />
                          This question has expired
                        </div>
                      )}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </div>

      {selectedQuestion && (
        <div className="selected-question-container">
          {/* Answer Section */}
          <div className="answer-section">
            <div className="answer-header">
              <h2>Your Answer</h2>
              <div className="question-text">
                {selectedQuestion?.question_text}
              </div>
            </div>

            <div className="smart-scoring">
              <div className="smart-scoring-header">
                <IoSparklesOutline className="scoring-icon" />
                <span className="smart-scoring-title">Smart Scoring</span>
              </div>
              <div className="smart-scoring-info">
                AI matches similar answers! Misspellings, synonyms, and abbreviations count as the same answer.
              </div>
            </div>

            {hasAnswered && (
              <div className="answer-status">
                <div className="answer-status-message">
                  <IoCheckmarkCircleOutline className="status-icon" />
                  You have already answered this question!
                </div>
                <div className="answer-success">
                  Great job! Your answer has been recorded.
                </div>
              </div>
            )}
          </div>



          {/* Only show answer distribution to admins */}
          {isAdmin && answerDistribution.length > 0 && (
            <div className="answer-distribution">
              <h3 className="heading-with-icon"><IoLockClosedOutline className="inline-icon" /> Admin View: Current Answer Distribution</h3>
              <p className="admin-notice">Only visible to administrators</p>
              <div className="distribution-list">
                {answerDistribution.map((item, idx) => (
                  <div key={idx} className="distribution-item">
                    <span className="answer-text">"{item.answer_text}"</span>
                    <span className="answer-count">{item.count} player{item.count !== 1 ? 's' : ''}</span>
                    <span className="points-per-person">{item.count} point{item.count !== 1 ? 's' : ''} each</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Grouping Details for admins */}
          {isAdmin && aiGrouping && aiGrouping.groupingDetails && aiGrouping.groupingDetails.length > 0 && (
            <div className="ai-grouping">
              <h3 className="heading-with-icon"><IoHardwareChipOutline className="inline-icon" /> Admin View: AI Answer Grouping</h3>
              <p className="admin-notice">
                AI {aiGrouping.aiEnabled ? 'successfully' : 'fallback'} grouped {aiGrouping.totalAnswers} answers into {aiGrouping.totalGroups} groups
              </p>
              <p className="ai-explanation">{aiGrouping.explanation}</p>
              
              <div className="grouping-list">
                {aiGrouping.groupingDetails.map((group, idx) => (
                  <div key={idx} className="grouping-item">
                    <div className="group-header">
                      <span className="group-title">Group {group.groupId}: "{group.representative}"</span>
                      <span className="group-points">{group.pointsPerAnswer} points each</span>
                    </div>
                    <div className="group-answers">
                      {group.answers.map((answer, answerIdx) => (
                        <div key={answerIdx} className="grouped-answer">
                          <span className="original-text">"{answer.originalText}"</span>
                          <span className="user-info">User {answer.userId}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Show leaderboard for all users except game show contestants (role_id 3) */}
          {currentUser && currentUser.role_id !== 3 && (
            <div className={`leaderboard-container ${isGameShowUser ? 'gameshow-leaderboard' : isHomeViewer ? 'homeviewer-leaderboard' : ''}`}>
              {/* Leaderboard Accordion */}
              <div className="leaderboard-accordion">
                <div 
                  className="accordion-header"
                  onClick={() => setIsLeaderboardOpen(!isLeaderboardOpen)}
                >
                  <h3 className="accordion-title">
                    <IoTrophyOutline className="inline-icon" />
                    Leaderboard
                  </h3>
                  <IoChevronDownOutline className={`accordion-icon ${isLeaderboardOpen ? 'open' : ''}`} />
                </div>

                <div className={`accordion-content ${isLeaderboardOpen ? 'open' : ''}`}>
                  <div className="leaderboard-tabs">
                    <button 
                      className={`tab-button ${leaderboardFilter === 'question' ? 'active' : ''}`}
                      onClick={() => handleLeaderboardFilterChange('question')}
                      disabled={!selectedQuestion}
                    >
                      This Question
                    </button>
                    <button 
                      className={`tab-button ${leaderboardFilter === 'today' ? 'active' : ''}`}
                      onClick={() => handleLeaderboardFilterChange('today')}
                    >
                      Today's Scores
                    </button>
                    <button 
                      className={`tab-button ${leaderboardFilter === 'alltime' ? 'active' : ''}`}
                      onClick={() => handleLeaderboardFilterChange('alltime')}
                    >
                      All-Time Scores
                    </button>
                  </div>

                  {leaderboard.length === 0 ? (
                    <div className="empty-leaderboard">
                      <p>{leaderboardFilter === 'question' ? 
                          "No answers submitted yet." :
                          leaderboardFilter === 'today' ? 
                          "No scores recorded today yet." :
                          "No scores recorded yet."
                        }</p>
                    </div>
                  ) : (
                    <div className="leaderboard-table-wrapper">
                      <table className="leaderboard-table">
                        <thead>
                          <tr>
                            <th>Rank</th>
                            <th>Player</th>
                            <th>Type</th>
                            <th>Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leaderboard.map((user, idx) => (
                            <tr key={user.username + idx} className={currentUser && user.username === currentUser.name ? 'current-user' : ''}>
                              <td>{idx + 1}</td>
                              <td>
                                {user.username}
                                {currentUser && user.username === currentUser.name && 
                                  <span className="current-user-badge">(You)</span>
                                }
                              </td>
                              <td>
                                <span className={`player-type-badge ${user.role_id === 2 ? 'home' : 'studio'}`}>
                                  {user.role_id === 2 ? 'Home' : 'Studio'}
                                </span>
                              </td>
                              <td className="points-cell">{user.awarded_points}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="leaderboard-footer">
                    <small>
                      {leaderboardFilter === 'question' ? 
                        'Showing scores for the current question only.' :
                        leaderboardFilter === 'today' ? 
                        'Showing total scores earned today across all questions.' :
                        'Showing lifetime total scores across all questions and sessions.'
                      }
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Answer Modal */}
      {showAnswerModal && modalQuestion && (
        <div className="modal-overlay">
          <div className="answer-modal">
            <div className="modal-header">
              <h3 className="heading-with-icon"><IoSparklesOutline className="inline-icon" /> Answer Question</h3>
              <div className="modal-close-btn" onClick={closeAnswerModal}>
                <IoCloseCircleOutline className="close-icon" />
              </div>
            </div>
            
            <div className="modal-content">
              <div className="modal-question">
                <p><strong>Question:</strong></p>
                <p className="question-text">{modalQuestion.question_text}</p>
              </div>
              
              <div className="modal-timer">
                <QuestionTimer 
                  endTime={modalQuestion.end_time} 
                  contestantTimer={modalQuestion.contestantTimer} 
                  homeViewerTimer={modalQuestion.homeViewerTimer} 
                  userRole={currentUser?.role_id} 
                />
              </div>



              {error && <div className="modal-error"><IoAlertCircleOutline className="inline-icon" /> {error}</div>}

              <form onSubmit={handleSubmitAnswer} className="modal-answer-form">
                <div className="answer-input-section">
                  <label htmlFor="modal-answer">Your Answer:</label>
                  <input
                    id="modal-answer"
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={isGameShowUser ? "Enter your answer..." : isHomeViewer ? "Enter your answer..." : "Type your answer freely..."}
                    required
                    autoFocus
                  />
                </div>
                
                <div className="modal-buttons">
                  <button type="submit" className="submit-answer-btn">
                    {isGameShowUser ? "Submit My Answer" : isHomeViewer ? "Submit My Answer" : "Submit Answer"}
                  </button>
                  <button type="button" onClick={closeAnswerModal} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GameShow; 